import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchMaterialPriceAndImagesForCurProdct, persistProductSelection } from '../../services';
import { getProductSizeValues } from '../../utils/sizeOptions';
import { getVariantSummary } from '../../utils/variantSummary';
import { addProductToCart } from './addProductToCart';
import { getAddToCartLabel } from './infoForProductById.utils';

const useInfoForProduct = () => {
  const dispatch = useAppDispatch();
  const curProduct = useAppSelector((state) => state.curProduct.productId);
  const selectedMaterial = useAppSelector((state) => state.curProduct.selectedMaterial);
  const selectedSize = useAppSelector((state) => state.curProduct.selectedSize);
  const price = useAppSelector((state) => state.curProduct.materialPriceAndImg?.[0]?.price ?? 0);
  const materialStatus = useAppSelector((state) => state.curProduct.imgStatus);
  const materialData = useAppSelector((state) => state.curProduct.materialPriceAndImg?.[0]);
  const userId = useAppSelector((state) => state.user.user?.id);
  const [addingToCart, setAddingToCart] = useState(false);
  const materialRequestRef = useRef<string | null>(null);
  const persistedSelectionRef = useRef<string | null>(null);
  const latestSelectionRef = useRef({
    productId: '',
    selectedMaterial: '',
    selectedSize: '',
  });
  const hasPendingSelectionSyncRef = useRef(false);
  const materialDataLoading = materialStatus === 'idle' || materialStatus === 'loading';
  const availableSizes = curProduct
    ? getProductSizeValues(curProduct.sizes, curProduct.materialPrices, selectedMaterial)
    : [];
  const requiresSize = availableSizes.length > 0;
  const variantSummary = getVariantSummary({
    material: materialData,
    selectedSize,
    productId: curProduct?.id,
    requiresSize,
    isLoading: materialDataLoading,
  });
  const addToCartLabel = getAddToCartLabel({
    materialDataLoading,
    requiresSize,
    selectedSize,
    canAddToCart: variantSummary.canAddToCart,
    isAvailable: variantSummary.isAvailable,
    isOutOfStock: variantSummary.isOutOfStock,
    price,
  });

  useEffect(() => {
    if (!curProduct?.id || !selectedMaterial) return;

    const hasMatchingMaterialData = materialData?.materialName === selectedMaterial;
    const requestKey = `${curProduct.id}:${selectedMaterial}`;

    if (hasMatchingMaterialData || materialRequestRef.current === requestKey) return;

    materialRequestRef.current = requestKey;
    dispatch(fetchMaterialPriceAndImagesForCurProdct({
      productId: curProduct.id,
      selectedMaterial,
    }));
  }, [curProduct?.id, dispatch, materialData?.materialName, selectedMaterial]);

  const flushProductSelection = useCallback(() => {
    if (!hasPendingSelectionSyncRef.current) return;

    const { productId, selectedMaterial: latestMaterial, selectedSize: latestSize } = latestSelectionRef.current;
    if (!productId || !latestMaterial) return;

    const selectionKey = `${productId}:${userId || 'guest'}:${latestMaterial}:${latestSize || ''}`;
    hasPendingSelectionSyncRef.current = false;
    persistedSelectionRef.current = selectionKey;
    dispatch(persistProductSelection({
      productId,
      userId,
      selectedMaterial: latestMaterial,
      selectedSize: latestSize,
    }));
  }, [dispatch, userId]);

  useEffect(() => () => {
    flushProductSelection();
  }, [flushProductSelection]);

  useEffect(() => {
    if (!curProduct?.id || !selectedMaterial) return;

    const selectionKey = `${curProduct.id}:${userId || 'guest'}:${selectedMaterial}:${selectedSize || ''}`;
    latestSelectionRef.current = {
      productId: curProduct.id,
      selectedMaterial,
      selectedSize: selectedSize || '',
    };

    if (persistedSelectionRef.current === null) {
      persistedSelectionRef.current = selectionKey;
      return;
    }

    if (persistedSelectionRef.current === selectionKey) return;

    const persistTimer = window.setTimeout(() => {
      hasPendingSelectionSyncRef.current = false;
      persistedSelectionRef.current = selectionKey;
      dispatch(persistProductSelection({
        productId: curProduct.id,
        userId,
        selectedMaterial,
        selectedSize,
      }));
    }, 350);
    hasPendingSelectionSyncRef.current = true;

    return () => window.clearTimeout(persistTimer);
  }, [curProduct?.id, dispatch, selectedMaterial, selectedSize, userId]);

  const handleAddToCart = async () => {
    if (!selectedMaterial) {
      message.error('Please select a material.');
      return;
    }

    if (!curProduct) {
      message.error('Product data is not available.');
      return;
    }

    if (materialDataLoading) {
      message.loading({ content: 'Product data is still loading. Please try again in a moment.', key: 'cart-product-loading', duration: 2 });
      return;
    }

    if (requiresSize && !selectedSize) {
      message.error('Please select a size.');
      return;
    }

    if (variantSummary.isOutOfStock || !variantSummary.isAvailable || price <= 0) {
      message.error(variantSummary.isOutOfStock ? 'This variant is out of stock.' : 'This variant is currently unavailable.');
      return;
    }

    try {
      setAddingToCart(true);
      await addProductToCart({ dispatch, curProduct, selectedMaterial, selectedSize, price, requiresSize, userId });
    } catch (error) {
      console.error('Error:', error);
      message.error('Failed to add item to cart.');
    } finally {
      setAddingToCart(false);
    }
  };

  return {
    curProduct,
    selectedMaterial,
    selectedSize,
    price,
    materialDataLoading,
    variantSummary,
    addToCartLabel,
    addingToCart,
    handleAddToCart,
  };
};

export default useInfoForProduct;
